import {
  TeamReportList,
  TeamReportListItem,
  GameReportListItem,
} from "../../../util/Requests";
import { NBAGameListItem } from "../../../util/types";
import { Team } from "../../../models/Team";
import dayjs from "dayjs";

const GAME_STATUS_FINAL = 3;
// Game reports are built for Portland's games only, both teams' reports (2026-10-05), so another team's list holds
// its games against Portland.
const GAME_REPORT_TEAM_NBA_ID = 1610612757;
const NBA_FIRST_SEASON_WITH_AVAILABLE_TEAM_REPORT = 2024;

// This function checks for completed games OR ones with a start-time after current time - Trevor found that the NBA API will sometimes fail to update the game status of completed games
// Preseason games count: POR's preseason games have game reports (the watcher builds them since 2026-10-07)
const filterCompletedOrStartedGames = ({
  gameStatus,
  gameDateTimeUTC,
}: NBAGameListItem): boolean =>
  gameStatus === GAME_STATUS_FINAL || dayjs().isAfter(gameDateTimeUTC);
// Helper function to get report label for document title
export const getReportLabel = (
  report: TeamReportListItem,
  type: string,
  team: Team | null
) => {
  if (!team) return "";

  const teamAbbrev = team.abbrev;

  if (type === "game") {
    // For game reports, we need to cast to GameReportListItem to access gameInfo
    const gameReport = report as GameReportListItem; // Type assertion since we know it's a game report
    if (gameReport.gameInfo) {
      const { opponent, date, isHome } = gameReport.gameInfo;
      const homeAwaySymbol = isHome ? "v" : "@";
      return `${teamAbbrev} Game Report ${homeAwaySymbol} ${opponent.abbrev}, ${date}`;
    }
    return `${teamAbbrev} Game Report`;
  } else if (type === "L5") {
    return `${teamAbbrev} Last 5 Report`;
  } else if (type === "L10") {
    return `${teamAbbrev} Last 10 Report`;
  } else if (type === "L20") {
    return `${teamAbbrev} Last 20 Report`;
  } else if (type === "playoff_season") {
    return `${teamAbbrev} Playoffs Report`;
  } else if (type === "preseason_season") {
    return `${teamAbbrev} Preseason Report`;
  } else if (type === "season") {
    return `${teamAbbrev} Season Report`;
  }

  return `${teamAbbrev} ${type.toUpperCase()} Report`;
};

// Helper function to build a GameReportListItem from header gameInfo
export const buildGameReportListItem = (
  reportId: string,
  gameId: string,
  gameInfo?: { isHome: boolean; date: string; opponent: string }
): GameReportListItem => {
  return {
    id: reportId,
    season: 0,
    gameInfo: {
      gameId,
      day: 0,
      month: 0,
      year: 0,
      season: 0,
      seasonType: "",
      isHome: gameInfo?.isHome ?? false,
      date: gameInfo?.date ?? "",
      opponent: { id: "", name: "", abbrev: gameInfo?.opponent ?? "" },
    },
  };
};

export const buildReportList = (
  games: NBAGameListItem[],
  team: Team,
  currentSeason: number
): TeamReportList => {
  const seasons = [];
  for (
    let year = NBA_FIRST_SEASON_WITH_AVAILABLE_TEAM_REPORT;
    year <= currentSeason;
    year++
  ) {
    seasons.push({
      id: `${team.abbrev}-season-${year}`,
      season: year,
    });
  }

  // Only this season's playoffs: the list can also hold an earlier season's games (the report being viewed)
  const hasPlayoffGames = games?.some(
    (g) =>
      (g.season ?? currentSeason) === currentSeason &&
      filterCompletedOrStartedGames(g) &&
      g.seasonType === "Playoffs"
  );

  return {
    season: seasons,
    playoff_season: hasPlayoffGames
      ? [{ id: `${team.abbrev}-playoff_season-${currentSeason}`, season: currentSeason }]
      : [],
    preseason_season: [],
    L5: [],
    L10: [],
    L20: [],
    game: (games?.filter(filterCompletedOrStartedGames) ?? [])
      .filter(
        (game) =>
          team.nbaId === GAME_REPORT_TEAM_NBA_ID ||
          game.homeTeam.teamId === GAME_REPORT_TEAM_NBA_ID ||
          game.awayTeam.teamId === GAME_REPORT_TEAM_NBA_ID
      )
      .map((game) => {
        const isHome = game.homeTeam.teamId === team.nbaId;
        const opponent = isHome ? game.awayTeam : game.homeTeam;
        const date = game.gameDateEst.split("T")[0];
        const gameDate = dayjs(date);
        return {
          id: `${team.abbrev}-game-${game.gameId}`,
          season: game.season,
          gameInfo: {
            gameId: game.gameId,
            day: gameDate.date(),
            month: gameDate.month() + 1,
            year: gameDate.year(),
            season: game.season ?? currentSeason,
            seasonType: game.seasonType,
            isHome,
            opponent: {
              id: opponent.teamId.toString(),
              name: opponent.teamName,
              abbrev: opponent.teamTricode,
            },
            date,
          },
        };
      })
      .sort((a, b) => b.gameInfo.date.localeCompare(a.gameInfo.date)),
  };
};

// Helper function to resolve report ID from URL parameters
export const resolveReportFromParams = (
  reportList: TeamReportList,
  searchParams: URLSearchParams,
  team: Team | null
) => {
  const reportParam = searchParams.get("report");
  const typeParam = searchParams.get("type");
  const seasonParam = searchParams.get("season");
  const dateParam = searchParams.get("date");

  // Direct report ID takes precedence
  if (reportParam) {
    const allReports = [
      ...reportList.season.map((r) => ({ ...r, type: "season" })),
      ...reportList.playoff_season.map((r) => ({ ...r, type: "playoff_season" })),
      ...(reportList.preseason_season ?? []).map((r) => ({ ...r, type: "preseason_season" })),
      ...(reportList.L5 ?? []).map((r) => ({ ...r, type: "L5" })),
      ...reportList.L10.map((r) => ({ ...r, type: "L10" })),
      ...reportList.L20.map((r) => ({ ...r, type: "L20" })),
      ...reportList.game.map((r) => ({ ...r, type: "game" })),
    ];
    const foundReport = allReports.find((r) => r.id === reportParam);
    if (foundReport) {
      return {
        id: foundReport.id,
        label: getReportLabel(foundReport, foundReport.type, team),
        season: foundReport.season,
      };
    }
  }

  // Type-based selection
  if (typeParam) {
    let reports: TeamReportListItem[] = [];
    switch (typeParam) {
      case "season":
        reports = reportList.season;
        break;
      case "playoff_season":
        reports = reportList.playoff_season;
        break;
      case "preseason_season":
        reports = reportList.preseason_season ?? [];
        break;
      case "L5":
        reports = reportList.L5 ?? [];
        break;
      case "L10":
        reports = reportList.L10;
        break;
      case "L20":
        reports = reportList.L20;
        break;
      case "game":
        reports = reportList.game;
        break;
    }

    if (reports.length > 0) {
      let selectedReport: TeamReportListItem;

      if (typeParam === "game") {
        if (dateParam) {
          // Find game by specific date (YYYY-MM-DD format)
          // Fall back to most recent game if no game is found for the given date
          const targetDate = dayjs(dateParam, "YYYY-MM-DD"); // e.g., "2025-04-11"
          selectedReport =
            reports.find((r) => {
              const gameReport = r as GameReportListItem;
              const gameDate = dayjs(gameReport.gameInfo.date);
              return gameDate.isSame(targetDate, "day");
            }) || reports[0];
        } else {
          // Get most recent game (first in descending sorted list)
          selectedReport = reports[0];
        }
      } else {
        // Handle season/L10/L20 reports
        if (seasonParam) {
          // Find specific season
          selectedReport =
            reports.find((r) => r.season.toString() === seasonParam) ||
            reports[0];
        } else {
          // Get most recent
          selectedReport = reports.sort((a, b) => b.season - a.season)[0];
        }
      }

      return {
        id: selectedReport.id,
        label: getReportLabel(selectedReport, typeParam, team),
        season: selectedReport.season,
      };
    }
  }

  // Default to most recent season report
  if (reportList.season.length > 0) {
    const mostRecentSeason = reportList.season.sort(
      (a, b) => b.season - a.season
    )[0];
    return {
      id: mostRecentSeason.id,
      label: getReportLabel(mostRecentSeason, "season", team),
      season: mostRecentSeason.season,
    };
  }

  return null;
};
